import { hydrateGmMonsterCatalog, loadGmMonsterCatalog } from "@/gm/gm-monster-catalog";
import { hydrateGmPartCatalog } from "@/gm/gm-part-catalog";
import { openGmTowerFloorEditor } from "@/pages/gm-routes";
import { TOWER_MAX_FLOOR } from "@/tower/tower-floors";
import {
  clearTowerFloorConfig,
  hydrateTowerFloorConfigs,
  isActiveTowerFloorConfig,
  listTowerFloorConfigs,
  setTowerFloorConfigEnabled,
  type TowerFloorConfig,
} from "@/tower/tower-floor-config";

function escapeHtml(text: string): string {
  return text
    .replaceAll("&", "&amp;")
    .replaceAll("<", "&lt;")
    .replaceAll(">", "&gt;")
    .replaceAll('"', "&quot;");
}

function spawnCount(cfg: TowerFloorConfig): number {
  return cfg.spawns.reduce((sum, r) => sum + r.count, 0);
}

function configMonsterSummary(cfg: TowerFloorConfig): string {
  const catalog = loadGmMonsterCatalog();
  const parts: string[] = [];
  for (const row of cfg.spawns) {
    const name = catalog.find((m) => m.id === row.monsterId)?.name ?? "?";
    parts.push(`${name} ×${row.count}`);
  }
  return parts.join(" · ") || "—";
}

function configListMeta(cfg: TowerFloorConfig): string {
  const count = spawnCount(cfg);
  const mult = cfg.powerMultiplier ?? 1;
  const sceneN = cfg.scenePlacements?.length ?? 0;
  const chunks = [configMonsterSummary(cfg)];
  if (mult !== 1) chunks.push(`พลัง ×${mult}`);
  if (sceneN > 0) chunks.push(`ฉาก ${sceneN} ชิ้น`);
  return `${count} ตัว · ${chunks.join(" · ")}`;
}

function firstFreeFloor(): number {
  const used = new Set(listTowerFloorConfigs().map((c) => c.floor));
  for (let f = 1; f <= TOWER_MAX_FLOOR; f += 1) {
    if (!used.has(f)) return f;
  }
  return TOWER_MAX_FLOOR;
}

function openFloorEditor(floor: number): void {
  openGmTowerFloorEditor(floor);
}

function renderConfiguredListItem(cfg: TowerFloorConfig): string {
  const active = isActiveTowerFloorConfig(cfg);
  const count = spawnCount(cfg);
  const statusClass = active ? "gm-tower-list-status--on" : "gm-tower-list-status--off";
  const statusLabel = active ? "เปิด" : cfg.enabled ? "ไม่พร้อม" : "ปิด";
  return `
    <li class="gm-tower-list-item" data-floor="${cfg.floor}">
      <div class="gm-tower-list-main">
        <p class="gm-tower-list-floor">ชั้น ${cfg.floor}</p>
        <p class="gm-tower-list-meta hint">${escapeHtml(configListMeta(cfg))}</p>
        <span class="gm-tower-list-status ${statusClass}">${statusLabel}</span>
      </div>
      <div class="gm-tower-list-actions">
        <label class="gm-tower-switch" title="เปิด/ปิดใช้งาน">
          <input type="checkbox" class="gm-tower-enable-toggle" data-floor="${cfg.floor}" ${cfg.enabled ? "checked" : ""} ${count === 0 ? "disabled" : ""} />
          <span class="gm-tower-switch-ui" aria-hidden="true"></span>
        </label>
        <button type="button" class="btn btn-ghost btn-sm gm-tower-edit-btn" data-floor="${cfg.floor}">แก้ไขฉาก</button>
        <button type="button" class="btn btn-ghost btn-sm gm-tower-delete-btn" data-floor="${cfg.floor}">ลบ</button>
      </div>
    </li>
  `;
}

function renderUnsetListItem(floor: number): string {
  return `
    <li class="gm-tower-list-item gm-tower-list-item--unset" data-floor="${floor}">
      <div class="gm-tower-list-main">
        <p class="gm-tower-list-floor">ชั้น ${floor}</p>
        <p class="gm-tower-list-meta hint">ยังไม่กำหนด · ใช้มอนสเตอร์เริ่มต้นในเกม</p>
        <span class="gm-tower-list-status gm-tower-list-status--default">ค่าเริ่มต้น</span>
      </div>
      <div class="gm-tower-list-actions">
        <button type="button" class="btn btn-primary btn-sm gm-tower-create-btn" data-floor="${floor}">สร้างฉาก</button>
      </div>
    </li>
  `;
}

function renderListHtml(): string {
  const byFloor = new Map(listTowerFloorConfigs().map((cfg) => [cfg.floor, cfg]));
  const rows: string[] = [];
  for (let floor = 1; floor <= TOWER_MAX_FLOOR; floor += 1) {
    const cfg = byFloor.get(floor);
    rows.push(cfg ? renderConfiguredListItem(cfg) : renderUnsetListItem(floor));
  }
  return `<ul class="gm-tower-list gm-tower-list--all-floors" role="list">${rows.join("")}</ul>`;
}

export function renderGmTowerSectionHtml(): string {
  return `
    <div class="gm-tower-page">
      <header class="gm-tower-toolbar">
        <div class="gm-tower-toolbar-text">
          <h2 class="gm-tower-toolbar-title">รายการกำหนดชั้นหอคอย</h2>
          <p class="hint">ชั้น 1–${TOWER_MAX_FLOOR} · กดสร้าง/แก้ไขฉากเปิดแท็บใหม่ · ฉากเป็นหลัก</p>
        </div>
        <button type="button" class="gm-monster-fab" id="gm-tower-add-entry" aria-label="สร้างรายการชั้น">+</button>
      </header>
      <div id="gm-tower-list-host">${renderListHtml()}</div>
      <p class="hint gm-tower-status" id="gm-tower-status" role="status"></p>
    </div>
  `;
}

function setStatus(root: HTMLElement, message: string, isError = false): void {
  const el = root.querySelector<HTMLElement>("#gm-tower-status");
  if (!el) return;
  el.textContent = message;
  el.classList.toggle("gm-tower-status--error", isError);
}

function refreshList(root: HTMLElement): void {
  const host = root.querySelector<HTMLElement>("#gm-tower-list-host");
  if (host) host.innerHTML = renderListHtml();
}

export function bindGmTowerSection(root: HTMLElement): () => void {
  void Promise.all([hydrateTowerFloorConfigs(), hydrateGmPartCatalog()]).then(() =>
    refreshList(root),
  );

  const onClick = (event: Event) => {
    const target = event.target as HTMLElement;

    if (target.id === "gm-tower-add-entry" || target.closest("#gm-tower-add-entry")) {
      openFloorEditor(firstFreeFloor());
      return;
    }

    const createBtn = target.closest<HTMLElement>(".gm-tower-create-btn");
    if (createBtn?.dataset.floor) {
      openFloorEditor(Math.floor(Number(createBtn.dataset.floor)));
      return;
    }

    const editBtn = target.closest<HTMLElement>(".gm-tower-edit-btn");
    if (editBtn?.dataset.floor) {
      openFloorEditor(Math.floor(Number(editBtn.dataset.floor)));
      return;
    }

    const deleteBtn = target.closest<HTMLElement>(".gm-tower-delete-btn");
    if (deleteBtn?.dataset.floor) {
      const floor = Math.floor(Number(deleteBtn.dataset.floor));
      void (async () => {
        await clearTowerFloorConfig(floor);
        refreshList(root);
        setStatus(root, `ลบรายการชั้น ${floor}`);
      })();
    }
  };

  const onChange = (event: Event) => {
    const target = event.target as HTMLElement;
    const toggle = target.closest<HTMLInputElement>(".gm-tower-enable-toggle");
    if (toggle?.dataset.floor) {
      const floor = Math.floor(Number(toggle.dataset.floor));
      void (async () => {
        await setTowerFloorConfigEnabled(floor, toggle.checked);
        refreshList(root);
        setStatus(root, `ชั้น ${floor} ${toggle.checked ? "เปิดใช้งาน" : "ปิดใช้งาน"}`);
      })();
    }
  };

  root.addEventListener("click", onClick);
  root.addEventListener("change", onChange);

  return () => {
    root.removeEventListener("click", onClick);
    root.removeEventListener("change", onChange);
  };
}

export async function prepareGmTowerSection(): Promise<void> {
  await hydrateGmMonsterCatalog();
  await hydrateGmPartCatalog();
  await hydrateTowerFloorConfigs();
}
